import { Message } from "primereact/message";
import React from "react";
import styled from "styled-components";

export type CvpMessageSeverity =
  | "info"
  | "success"
  | "warn"
  | "error"
  | "neutral";

export interface CvpMessageProps {
  severity: CvpMessageSeverity;
  /**
   * Icon to display. Pass a PrimeIcon class string (e.g. `"pi-clock"`) and it
   * will be wrapped in an `<i className="pi pi-clock" />`. Pass a ReactNode to
   * render custom JSX. Omit to use PrimeReact's default severity icon.
   */
  icon?: React.ReactNode | string;
  /** Simple inline text body. Use `content` for richer layouts. */
  text?: React.ReactNode;
  /** Custom content block. Replaces `text` when provided. */
  content?: React.ReactNode;
  className?: string;
}

/**
 * Banner-style message with a coloured left-accent border, matching the
 * cvp leave / status banners. Wraps PrimeReact's `Message` (singular) and
 * adds the missing `border-left` per severity (the lara theme ships
 * background + colour but `border-width: 0`).
 *
 * Use this anywhere you want a static info / success / warn / error /
 * neutral banner.
 */
export const CvpMessage: React.FC<CvpMessageProps> = ({
  severity,
  icon,
  text,
  content,
  className,
}) => {
  // Resolve string-shorthand icons to JSX so PrimeReact renders them.
  const iconNode: React.ReactNode | undefined =
    typeof icon === "string" ? <i className={`pi ${icon}`} /> : icon;

  // PrimeReact doesn't have a "neutral" severity — fall back to "info" and let
  // our CSS class re-skin it as grey.
  const primeSeverity: "info" | "success" | "warn" | "error" =
    severity === "neutral" ? "info" : severity;

  return (
    <StyledMessage
      severity={primeSeverity}
      icon={iconNode}
      text={text}
      content={content}
      className={`cvp-message cvp-message--${severity}${
        className ? ` ${className}` : ""
      }`}
    />
  );
};

// PrimeReact's lara theme ships .p-inline-message-* rules with
// `border-width: 0`. We override here to add the 6px left-accent border per
// severity. The double `&&` boosts specificity past the theme rules.
const StyledMessage = styled(Message)`
  &&.p-inline-message {
    width: 100%;
    margin: 0;
    padding: 1.5rem 1rem;
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 0.75rem;
    border-radius: 6px;
    border: none;
    text-align: left;
  }

  &&.p-inline-message.p-inline-message-warn {
    border-left: 6px solid #cc8925;
  }
  &&.p-inline-message.p-inline-message-success {
    border-left: 6px solid #1ea97c;
  }
  &&.p-inline-message.p-inline-message-error {
    border-left: 6px solid #ff5757;
  }
  &&.p-inline-message.p-inline-message-info {
    border-left: 6px solid #3b82f6;
  }

  /* Neutral re-skins the info severity into greyscale. We chain
     .p-inline-message so the selector beats the blue
     .p-inline-message.p-inline-message-info rule on specificity. */
  &&.p-inline-message.cvp-message--neutral {
    background: var(--surface-100, #f5f5f5);
    border-left: 6px solid var(--surface-400, #9ca3af);
    color: var(--text-color);
  }
  &&.p-inline-message.cvp-message--neutral .p-inline-message-icon {
    color: var(--text-color-secondary);
  }
`;
